/**
 * Authentication Error Handler
 *
 * This module handles authentication errors by preserving error messages
 * and redirecting users to the login page without losing error context.
 */

const AUTH_ERROR_KEY = 'auth-error-message';
const AUTH_REDIRECT_FLAG = 'auth-redirect-pending';

/**
 * Check if response contains authentication error
 * @param {Object} data - Response data from API
 * @returns {boolean} - True if authentication error detected
 */
export const isAuthenticationError = (data) => {
    if (data && typeof data === 'object') {
        // Check for Laravel's AuthenticationException message
        if (data.message === 'Unauthenticated.' ||
            (data.exception && data.exception.includes('AuthenticationException'))) {
            return true;
        }
    }
    return false;
};

/**
 * Handle authentication error by preserving the error message
 * and redirecting to login page
 * @param {Object} data - Response data containing error information
 */
export const handleAuthenticationError = (data) => {
    // Store error message in localStorage to preserve it across page reload
    const errorMessage = data.message || 'Your session has expired. Please login again.';
    localStorage.setItem(AUTH_ERROR_KEY, errorMessage);
    localStorage.setItem(AUTH_REDIRECT_FLAG, 'true');

    // Clear the authentication token
    localStorage.removeItem('token-afrisocialworld');

    // Redirect to login page
    window.location.href = '/login';
};

/**
 * Get stored authentication error message and clear it
 * @returns {string|null} - The stored error message or null
 */
export const getAuthErrorMessage = () => {
    const message = localStorage.getItem(AUTH_ERROR_KEY);
    const redirectFlag = localStorage.getItem(AUTH_REDIRECT_FLAG);

    if (redirectFlag === 'true') {
        // Clear the stored values
        localStorage.removeItem(AUTH_ERROR_KEY);
        localStorage.removeItem(AUTH_REDIRECT_FLAG);
        return message;
    }

    return null;
};

/**
 * Check if response contains authentication error and handle it
 * @param {Object} data - Response data from API
 * @returns {boolean} - True if authentication error was detected and handled
 */
export const checkAndHandleAuthError = (data) => {
    if (isAuthenticationError(data)) {
        handleAuthenticationError(data);
        return true;
    }
    return false;
};

const authErrorHandler = {
    isAuthenticationError,
    handleAuthenticationError,
    getAuthErrorMessage,
    checkAndHandleAuthError
};

export default authErrorHandler;
